import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from 'modules/state-mgmt/store';
import { getRedirectPath } from 'utils/routeGuard';

interface UseRouteGuardOptions {
  enabled?: boolean;
}

const useRouteGuard = ({ enabled = true }: UseRouteGuardOptions = {}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { user, onboarding, cache } = useAppSelector(state => ({
    user: state.user,
    onboarding: state.onboarding,
    cache: state.cache,
  }));

  // Compute redirect synchronously so we can block rendering immediately
  const redirectPath = useMemo(() => {
    if (!enabled) return null;
    return getRedirectPath({
      currentPath: location.pathname,
      user,
      onboarding,
      cache,
    });
  }, [location.pathname, enabled, user, onboarding, cache]);

  useEffect(() => {
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  }, [redirectPath]); // eslint-disable-line react-hooks/exhaustive-deps

  return { isRedirecting: !!redirectPath };
};

export default useRouteGuard;
